// Реестр страниц лендинга, единственный источник правды для <title>,
// description, canonical, OG, robots, JSON-LD и sitemap.
//
// Кто читает:
//   • scripts/prerender.mjs, кладёт теги в статический HTML каждой страницы
//     и генерирует dist/sitemap.xml (поисковик видит всё без JS);
//   • src/seo/RouteSeo.tsx, обновляет <head> при клиентских переходах.
//
// Новая страница = новая запись здесь + <Route> в App.tsx. Без записи
// пререндер упадёт с понятной ошибкой (см. prerender.mjs), чтобы страница
// не ушла в прод без метаданных.

import {
  breadcrumbs,
  cashierApp,
  homeFaq,
  organization,
  partnerProduct,
  resstroProduct,
  supportFaq,
  techArticle,
  webSite,
  type JsonLd,
} from './jsonld';
import { DEFAULT_OG_IMAGE, PARTNER_TRIAL_DAYS, PRICE_FROM_RUB, TRIAL_DAYS } from './site';
import { formatRub } from '@/utils/format';
import { DOCS, DOCS_PATH, docPath } from '@/data/docs/meta';

export type ChangeFreq = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';

export interface SeoPage {
  /** Путь без завершающего слэша (кроме корня). */
  path: string;
  title: string;
  description: string;
  /** Яндекс всё ещё читает keywords; Google игнорирует. Только для ключевых страниц. */
  keywords?: string;
  /** Относительный путь к OG-картинке 1200×630 (public/og/*). */
  ogImage?: string;
  /** noindex,nofollow + не попадает в sitemap. */
  noindex?: boolean;
  /**
   * 'full', рендерим разметку страницы в HTML (по умолчанию);
   * 'head', только <head>, тело рисует клиент (личные страницы с localStorage/токенами);
   * 'redirect', в nginx стоит 301, файл не генерируем.
   */
  prerender?: 'full' | 'head' | 'redirect';
  /** Модуль страницы в src/, для <link rel="modulepreload"> ленивого чанка. */
  chunk?: string;
  /**
   * Динамический маршрут: запись отвечает и за `path/<что-угодно>`
   * (`/v` → `/v/abc123`). Пререндер кладёт один `path/index.html`, nginx
   * отдаёт его на любой вложенный адрес (см. nginx.conf).
   */
  matchPrefix?: boolean;
  /** Дополнительные префиксы того же экрана (`/venues`, `/establishments` → `/v`). Только с matchPrefix. */
  aliases?: string[];
  /** Файлы/папки, чей последний git-коммит становится <lastmod> в sitemap. */
  sources?: string[];
  changefreq?: ChangeFreq;
  priority?: number;
  /** Подпись в BreadcrumbList; главная, без крошек. */
  breadcrumb?: string;
  /** Крошки вложенной страницы после «Главной»; если задано, заменяет breadcrumb. */
  breadcrumbTrail?: Array<{ name: string; path: string }>;
  /** Дополнительные сущности schema.org поверх крошек. */
  jsonLd?: () => JsonLd[];
}

const HOME_SOURCES = ['src/pages/Home.tsx', 'src/components/sections', 'src/data'];

export const SEO_PAGES: SeoPage[] = [
  {
    path: '/',
    title: 'Resstro: система автоматизации ресторана. Касса, админка и QR-меню',
    description: `Касса, админ-панель и электронное QR-меню для ресторана и кафе в одной подписке: ${formatRub(PRICE_FROM_RUB)}/мес за заведение. Серверы в России, 0 % с оборота, ${TRIAL_DAYS} дней бесплатно без карты.`,
    keywords:
      'система автоматизации ресторана, программа для кафе, касса для ресторана, облачная касса, электронное меню QR, учёт в общепите, аналог iiko, аналог r_keeper, аналог Poster',
    sources: HOME_SOURCES,
    changefreq: 'weekly',
    priority: 1,
    jsonLd: () => [organization(), webSite(), resstroProduct(), homeFaq()],
  },
  {
    path: '/downloads',
    title: 'Скачать кассу Resstro для Windows, Android, iPhone и iPad',
    description:
      'Приложение Resstro Касса: установщик для Windows-моноблоков, APK для Android-планшетов и терминалов, версия для iPhone и iPad в App Store. Приём заказов, оплаты, печать чеков.',
    keywords: 'скачать кассу для ресторана, касса Android APK, касса Windows, касса iPad, приложение официанта',
    ogImage: '/og/downloads.png',
    chunk: 'src/pages/Downloads.tsx',
    sources: ['src/pages/Downloads.tsx', 'src/data/downloads.ts'],
    changefreq: 'monthly',
    priority: 0.8,
    breadcrumb: 'Скачать приложение',
    jsonLd: () => [cashierApp()],
  },
  {
    path: '/suppliers',
    title: 'Поставщикам: продавайте продукты ресторанам через Resstro',
    description: `Кабинет поставщика Resstro: ваш каталог видят рестораны и кафе и заказывают прямо из кассы. Заявки, отсрочки, дебиторка и логистика в одном окне. ${PARTNER_TRIAL_DAYS} дня бесплатно, далее 2 999 ₽/мес.`,
    keywords: 'поставщик продуктов для ресторанов, B2B каталог для общепита, заказы от ресторанов, поставки в кафе',
    ogImage: '/og/suppliers.png',
    chunk: 'src/pages/Suppliers.tsx',
    changefreq: 'monthly',
    priority: 0.8,
    breadcrumb: 'Поставщикам',
    jsonLd: () => [
      partnerProduct(
        'Resstro для поставщиков',
        '/suppliers',
        'Кабинет поставщика: каталог товаров, заявки от ресторанов, условия оплаты и логистика.',
      ),
    ],
  },
  {
    path: '/wholesalers',
    title: 'Resstro Оптовик: учёт производства и оптовых продаж',
    description: `Кабинет для производства и оптовой торговли: техкарты, приходные накладные, склад, продажи с чеком, себестоимость и маржа. ${PARTNER_TRIAL_DAYS} дня бесплатно, далее 2 999 ₽/мес.`,
    keywords: 'учёт производства, оптовые продажи, техкарты, себестоимость продукции, складской учёт для пищевого производства',
    ogImage: '/og/wholesalers.png',
    chunk: 'src/pages/Wholesalers.tsx',
    changefreq: 'monthly',
    priority: 0.8,
    breadcrumb: 'Оптовикам',
    jsonLd: () => [
      partnerProduct(
        'Resstro Оптовик',
        '/wholesalers',
        'Кабинет производства и оптовой торговли: техкарты, приходные накладные, склад, продажи с чеком, себестоимость и маржа.',
      ),
    ],
  },
  {
    path: '/support',
    title: 'Поддержка Resstro: WhatsApp, email и ответы на частые вопросы',
    description:
      'Служба поддержки Resstro: WhatsApp и email, режим работы, инструкции по подключению кассы, оплате и настройке, ответы на частые вопросы.',
    ogImage: '/og/support.png',
    chunk: 'src/pages/Support.tsx',
    sources: ['src/pages/Support.tsx', 'src/data/supportFaq.ts'],
    changefreq: 'monthly',
    priority: 0.7,
    breadcrumb: 'Поддержка',
    jsonLd: () => [supportFaq()],
  },
  {
    path: '/contacts',
    title: 'Контакты | Resstro',
    description:
      'Контакты Resstro: реквизиты ИП Раджабов Р. Э. (ИНН 051707862851), email, телефон и канал поддержки.',
    chunk: 'src/pages/Contacts.tsx',
    changefreq: 'yearly',
    priority: 0.5,
    breadcrumb: 'Контакты',
    jsonLd: () => [organization()],
  },
  {
    path: '/changelog',
    title: 'История обновлений | Resstro',
    description:
      'Что нового в Resstro: фичи, улучшения и исправления. Открытая история релизов.',
    chunk: 'src/pages/Changelog.tsx',
    changefreq: 'weekly',
    priority: 0.5,
    breadcrumb: 'История обновлений',
  },
  {
    path: '/roadmap',
    title: 'Roadmap | Resstro',
    description:
      'Открытая дорожная карта Resstro: что уже сделано, что в работе и что планируется в ближайших кварталах.',
    chunk: 'src/pages/Roadmap.tsx',
    changefreq: 'monthly',
    priority: 0.5,
    breadcrumb: 'Roadmap',
  },

  // ── Документация: хаб + по странице на статью (nginx отдаёт только пререндеренные адреса) ──
  {
    path: DOCS_PATH,
    title: 'Документация Resstro: касса, админ-панель и QR-меню',
    description:
      'Инструкции по Resstro: быстрый старт, админ-панель, касса для iPad, Android и Windows, приложение официанта, электронное меню, кабинеты поставщика и оптовика.',
    chunk: 'src/pages/Docs.tsx',
    sources: ['src/pages/Docs.tsx', 'src/data/docs'],
    changefreq: 'weekly',
    priority: 0.6,
    breadcrumb: 'Документация',
  },
  ...DOCS.map(
    (doc): SeoPage => ({
      path: docPath(doc.slug),
      title: doc.seoTitle,
      description: doc.seoDescription,
      chunk: 'src/pages/DocArticle.tsx',
      sources: ['src/data/docs'],
      changefreq: 'monthly',
      priority: 0.5,
      breadcrumbTrail: [
        { name: 'Документация', path: DOCS_PATH },
        { name: doc.title, path: docPath(doc.slug) },
      ],
      jsonLd: () => [techArticle(doc.seoTitle, docPath(doc.slug), doc.seoDescription)],
    }),
  ),

  // ── Юридические документы ──
  {
    path: '/legal/offer',
    title: 'Публичная оферта | Resstro',
    description:
      'Условия пользования сервисом Resstro: предмет оферты, тарифы, оплата, права сторон и порядок отказа.',
    chunk: 'src/pages/Offer.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumb: 'Публичная оферта',
  },
  {
    path: '/legal/privacy',
    title: 'Политика конфиденциальности | Resstro',
    description:
      'Как Resstro собирает, хранит и защищает персональные данные пользователей и гостей заведений (152-ФЗ).',
    chunk: 'src/pages/Privacy.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumb: 'Политика конфиденциальности',
  },
  {
    path: '/legal/consent',
    title: 'Согласие на обработку персональных данных | Resstro',
    description:
      'Текст согласия на обработку персональных данных, предоставляемого пользователем при регистрации в Resstro.',
    chunk: 'src/pages/legal/Consent.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumb: 'Согласие на обработку данных',
  },
  {
    path: '/legal/cookies',
    title: 'Политика использования cookies | Resstro',
    description:
      'Какие файлы cookies использует Resstro, зачем они нужны и как ими управлять.',
    chunk: 'src/pages/legal/CookiesPolicy.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumb: 'Политика cookies',
  },
  {
    path: '/legal/return',
    title: 'Возврат средств | Resstro',
    description:
      'Условия возврата оплаты за подписку Resstro: сроки, порядок обращения и случаи, когда возврат не производится.',
    chunk: 'src/pages/legal/Return.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumb: 'Возврат средств',
  },
  {
    path: '/legal/security',
    title: 'Безопасность данных | Resstro',
    description:
      'Как защищены данные в Resstro: серверы в России, шифрование, резервные копии, разграничение доступа.',
    chunk: 'src/pages/legal/Security.tsx',
    changefreq: 'yearly',
    priority: 0.3,
    breadcrumb: 'Безопасность',
  },

  // ── Личные страницы: в индекс не нужны, тело рисует клиент ──
  {
    path: '/signup',
    title: 'Регистрация | Resstro',
    description: `Создайте аккаунт Resstro: ${TRIAL_DAYS} дней полного доступа бесплатно, без карты.`,
    noindex: true,
    prerender: 'head',
    chunk: 'src/pages/Signup.tsx',
  },
  {
    path: '/login',
    title: 'Вход | Resstro',
    description: 'Вход в личный кабинет владельца заведения Resstro.',
    noindex: true,
    prerender: 'head',
    chunk: 'src/pages/Login.tsx',
  },
  {
    path: '/cabinet',
    title: 'Личный кабинет | Resstro',
    description: 'Подписка, оплата и вход в админ-панель Resstro.',
    noindex: true,
    prerender: 'head',
    chunk: 'src/pages/Cabinet.tsx',
  },

  // ── Universal link гостевого приложения: https://resstro.ru/v/<id> ──
  // С установленным People iOS открывает ссылку в приложении; в браузере -
  // карточка заведения и кнопки «Открыть в приложении» / App Store.
  {
    path: '/v',
    title: 'Заведение в приложении Resstro People',
    description: 'Меню, заказ к столу и бронирование: откройте заведение в приложении Resstro People.',
    noindex: true,
    prerender: 'head',
    matchPrefix: true,
    aliases: ['/venues', '/establishments'],
    chunk: 'src/pages/OpenInApp.tsx',
  },

  // ── Старые адреса: 301 в nginx (см. nginx.conf), клиентский <Navigate>, страховка ──
  { path: '/legal/data', title: 'Согласие на обработку персональных данных | Resstro', description: '', noindex: true, prerender: 'redirect' },
  { path: '/cabinet/billing', title: 'Личный кабинет | Resstro', description: '', noindex: true, prerender: 'redirect' },
  { path: '/coming-soon', title: 'Личный кабинет | Resstro', description: '', noindex: true, prerender: 'redirect' },
];

/** Страница 404: отдаётся nginx-ом со статусом 404 из dist/404.html. */
export const NOT_FOUND_PAGE: SeoPage = {
  path: '/404',
  title: 'Страница не найдена | Resstro',
  description: 'Страница не существует или была перемещена.',
  noindex: true,
  // Только <head>: nginx отдаёт 404.html на любой неизвестный адрес, а страница
  // показывает запрошенный pathname, пререндер с /404 внутри не совпал бы при гидрации.
  prerender: 'head',
  chunk: 'src/pages/NotFound.tsx',
};

const BY_PATH = new Map(SEO_PAGES.map((p) => [p.path, p]));
const PREFIX_PAGES = SEO_PAGES.filter((p) => p.matchPrefix);

/** Убирает завершающий слэш, query и hash: '/suppliers/?utm=1' → '/suppliers'. */
export function normalizePath(pathname: string): string {
  const clean = pathname.split(/[?#]/)[0] || '/';
  if (clean.length > 1 && clean.endsWith('/')) return clean.replace(/\/+$/, '') || '/';
  return clean;
}

/** Запись реестра по адресу или NOT_FOUND_PAGE. */
export function resolveSeoPage(pathname: string): SeoPage {
  const clean = normalizePath(pathname);
  return (
    BY_PATH.get(clean) ??
    PREFIX_PAGES.find((p) => [p.path, ...(p.aliases ?? [])].some((prefix) => clean.startsWith(`${prefix}/`))) ??
    NOT_FOUND_PAGE
  );
}

/** Все префиксы, за которые отвечает реестр (для проверки маршрутов App.tsx в prerender.mjs). */
export function knownRoutePrefixes(): string[] {
  return SEO_PAGES.flatMap((p) => [p.path, ...(p.matchPrefix ? p.aliases ?? [] : [])]);
}

/** Страницы для sitemap.xml: индексируемые и с собственным HTML. */
export function sitemapPages(): SeoPage[] {
  return SEO_PAGES.filter((p) => !p.noindex && p.prerender !== 'redirect');
}

/** Полный набор JSON-LD страницы: крошки (кроме главной) + свои сущности. */
export function pageJsonLd(page: SeoPage): JsonLd[] {
  const nodes: JsonLd[] = [];
  const trail = page.breadcrumbTrail ?? (page.breadcrumb ? [{ name: page.breadcrumb, path: page.path }] : null);
  if (trail && !page.noindex) {
    nodes.push(breadcrumbs([{ name: 'Главная', path: '/' }, ...trail]));
  }
  if (page.jsonLd) nodes.push(...page.jsonLd());
  return nodes;
}

export { DEFAULT_OG_IMAGE };
